'use strict';

import moment from 'moment-timezone';

import icalParser from '~/shared/src/utilities/icalParser';
import {
    isIntlNumberFormatCurrency,
    isIntlNumberFormatUnit
} from '~/shared/src/utilities/valueTesters';
import {__} from '~/shared/src/utilities/messageCatalog';

const dateOptions : Intl.DateTimeFormatOptions     = { day: '2-digit', month: '2-digit', year: 'numeric' },
      timeOptions : Intl.DateTimeFormatOptions     = { hour12: false, hour: '2-digit', minute: '2-digit' },
      timeLongOptions : Intl.DateTimeFormatOptions = { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' };

const isIntlNumberFormatCurrencyCache : { [ value : string ] : boolean } = {};
const isIntlNumberFormatCurrencyCached = ( value : string ) => {
    if ( !isIntlNumberFormatCurrencyCache[ value ] ) {
        isIntlNumberFormatCurrencyCache[ value ] = isIntlNumberFormatCurrency( value );
    }

    return isIntlNumberFormatCurrencyCache[ value ];
};

const isIntlNumberFormatUnitCache : { [ value : string ] : boolean } = {};
const isIntlNumberFormatUnitCached = ( value : string ) => {
    if ( !isIntlNumberFormatUnitCache[ value ] ) {
        isIntlNumberFormatUnitCache[ value ] = isIntlNumberFormatUnit( value );
    }

    return isIntlNumberFormatUnitCache[ value ];
};

/**
 * convert
 *
 * @param  {Boolean|Number|String}  value                   The value to format.
 *
 * @return {String}                                         The formatted value.
 */
export const formatBool = ( value : boolean | number | string ) : string => {
    let retVal;

    value = typeof value === 'string'
        ? value.toLowerCase()
        : value;

    switch ( value ) {
        case undefined:
        case null:
        case '':
        case 'null':
            retVal = '';
            break;
        case false:
        case 0:
        case '0':
        case 'f':
        case 'false':
            retVal = 'false';
            break;
        default:
            retVal = 'true';
            break;
    }

    return retVal;
};

/**
 * Format given value by dividing with 1024 as often as it is greater than 1024
 * and adds the appropriate SI-prefix therefor.
 *
 * @param  {number}         value                   The value to format.
 * @param  {number}         precision               An integer specifying the number of significant digits.
 *
 * @return {String}                                         The formatted value.
 */
export const formatBytes = ( value : number, precision : number = 0, divider : number = 1024 ) => {
    const siPrefixes = [ 'k', 'M', 'G', 'T', 'P', 'E', 'Z', 'Y' ];

    let unit : string = 'B',
        divideCount   = 0;

    while ( value >= divider ) {
        value /= divider;
        divideCount++;
    }

    if ( divideCount > 0 ) {
        unit = siPrefixes[ divideCount - 1 ] + 'B';
    }

    return Intl.NumberFormat( undefined, { minimumFractionDigits: precision, maximumFractionDigits: precision } ).format( value ) + ' ' + unit;
};

/**
 * Format given bytes by dividing with 1024 as often as it is greater than 1024
 * and adds the appropriate SI-prefix therefor.
 *
 * @param  value                     The value to format.
 * @param  precision                 An integer specifying the number of significant digits.
 *
 * @return {String}                                           The formatted value.
 */
export const formatMilliseconds = ( value : number, precision : number = 0 ) : string => {
    const retValParts = [];

    let hours   = 0,
        minutes = 0,
        seconds;

    precision = Math.pow( 10, precision );

    seconds = Math.round( ( value / 1000 ) * precision ) / precision;

    if ( seconds > 60 ) {
        minutes = Math.floor( seconds / 60 );
        seconds = seconds - ( minutes * 60 );
    }

    if ( minutes > 60 ) {
        hours = Math.floor( minutes / 60 );
        minutes = minutes % 60;
    }

    if ( seconds < 10 && ( minutes > 0 || hours > 0 ) ) {
        retValParts.push( '0' + seconds );
    }
    else {
        retValParts.push( seconds );
    }
    retValParts.push( ' sec' );

    if ( minutes > 0 || hours > 0 ) {
        if ( minutes < 10 && hours > 0 ) {
            retValParts.unshift( '0' + minutes + ':' );
        }
        else {
            retValParts.unshift( minutes + ':' );
        }
        retValParts[ retValParts.length - 1 ] = ' min';
    }

    if ( hours > 0 ) {
        retValParts.unshift( hours + ':' );
        retValParts[ retValParts.length - 1 ] = ' hour';
    }

    return retValParts.join( '' );
};

/**
 * Returns the converted date for timezone.
 *
 * @param  {Date|String}            value                     The value to format.
 * @param  {String}                 timezone                  The timezone to format the value at.
 *
 * @return {String}                                           The formatted value.
 */
export const formatDate = ( value : Date | string, timezone : string = 'Europe/Berlin' ) : string => {
    let retVal = '';

    const date : Date                               = typeof value === 'string' ? moment( value, 'YYYY-MM-DD' ).toDate() : value,
          cDateOptions : Intl.DateTimeFormatOptions = Object.assign( { timeZone: timezone }, dateOptions );

    if ( date && !isNaN( date.getTime() ) ) {
        retVal = new Intl.DateTimeFormat( undefined, cDateOptions ).format( date );
    }

    return retVal;
}

/**
 * Returns the converted float.
 *
 * @param {number|string}           value                           The value to format.
 * @param {string}                  [unit='']                       The unit of the value
 * @param {number}                  [fractionDigits=0]    The number of fraction digits to show the value with. Only applicable for non currency units!
 *
 * @return {String}                                                 The formatted value.
 */
export const formatDouble = ( value : number | string, unit : string = '', fractionDigits : number = 0 ) : string => {
    let retVal = '';

    if ( value !== undefined && value !== null && ( typeof value === 'number' || value.trim() !== '' ) ) {
        const numberValue = Number( value );

        if ( unit ) {
            // currency
            if ( isIntlNumberFormatCurrencyCached( unit ) ) {
                retVal = Intl.NumberFormat( undefined, { style: 'currency', currency: unit } ).format( numberValue );
            }

            // unit
            else if ( isIntlNumberFormatUnitCached( unit ) ) {
                // TODO: check this
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                retVal = Intl.NumberFormat( undefined, { style: 'unit', unit, minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits } ).format( numberValue );
            }

            // suffix
            else {
                retVal = Intl.NumberFormat( undefined, { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits } ).format( numberValue ) + ` ${unit}`;
            }
        }
        else {
            retVal = Intl.NumberFormat( undefined, { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits } ).format( numberValue );
        }
    }

    return retVal;
}

/**
 * Returns the converted duration.
 *
 * @param  {Number|String}          value                     The value to format.
 *
 * @return {String}                                           The formatted value.
 */
export const formatDuration = ( value : number | string ) : string => {
    let retVal = '';

    if ( value !== undefined && value !== null && ( typeof value === 'number' || ( value.trim() !== '' && !isNaN( Number( value ) ) ) ) ) {
        const locale                   = moment.localeData( navigator.language ),
              durationParts : string[] = [],
              dayDuration              = 86400000,
              hourDuration             = 3600000,
              minuteDuration           = 60000,
              secondDuration           = 1000,
              duration                 = Number( value ) * 1000,
              days                     = Math.floor( duration / dayDuration ),
              hours                    = Math.floor( ( duration - days * dayDuration ) / hourDuration ),
              minutes                  = Math.floor( ( duration - days * dayDuration - hours * hourDuration ) / minuteDuration ),
              seconds                  = Math.floor( ( duration - days * dayDuration - hours * hourDuration - minutes * minuteDuration ) / secondDuration ),
              milliSeconds             = duration - days * dayDuration - hours * hourDuration - minutes * minuteDuration - seconds * secondDuration;

        if ( days ) {
            durationParts.push(
                locale.relativeTime(
                    days,
                    true,
                    days === 1 ? 'd' : 'dd',
                    false
                )
            );
        }

        if ( hours || ( durationParts.length && ( minutes || seconds || milliSeconds ) ) ) {
            durationParts.push(
                locale.relativeTime(
                    hours,
                    true,
                    hours === 1 ? 'h' : 'hh',
                    false
                )
            );
        }

        if ( minutes || ( durationParts.length && ( seconds || milliSeconds ) ) ) {
            durationParts.push(
                locale.relativeTime(
                    minutes,
                    true,
                    minutes === 1 ? 'm' : 'mm',
                    false
                )
            );
        }

        if ( seconds || ( durationParts.length && milliSeconds ) ) {
            durationParts.push(
                locale.relativeTime(
                    seconds,
                    true,
                    seconds === 1 ? 's' : 'ss',
                    false
                )
            );
        }

        if ( milliSeconds ) {
            durationParts.push( `${milliSeconds} ms` );
        }

        retVal = durationParts.join( ', ' );
    }

    return retVal;
}

/**
 * Returns the converted integer.
 *
 * @param  {Number|String}                 value                     The value to format.
 * @param {String}                  unit                      The unit of the value
 *
 * @return {String}                                           The formatted value.
 */
export const formatInteger = ( value : number | string, unit = '' ) : string => {
    let retVal = '';

    if ( value !== undefined && value !== null && ( typeof value === 'number' || value.trim() !== '' ) ) {
        const numberValue = Number( value );

        if ( unit ) {
            // currency
            if ( isIntlNumberFormatCurrencyCached( unit ) ) {
                retVal = Intl.NumberFormat( undefined, { style: 'currency', currency: unit } ).format( numberValue );
            }

            // unit
            else if ( isIntlNumberFormatUnitCached( unit ) ) {
                // TODO: check this
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                retVal = Intl.NumberFormat( undefined, { style: 'unit', unit } ).format( numberValue );
            }

            // suffix
            else {
                retVal = `${Intl.NumberFormat().format( numberValue )} ${unit}`;
            }
        }
        else {
            retVal = Intl.NumberFormat().format( numberValue );
        }
    }

    return retVal;
}

/**
 * Returns the converted time for timezone.
 *
 * @param  {Date|String}                 value                     The value to format.
 * @param  {String}                 timezone                  The timezone to format the value at.
 *
 * @return {String}                                           The formatted value.
 */
export const formatTime = ( value : Date | string, timezone : string = 'Europe/Berlin' ) : string => {
    let retVal = '';

    const date : Date                               = typeof value === 'string' ? moment( value, 'HH:mm:ss' ).toDate() : value,
          cTimeOptions : Intl.DateTimeFormatOptions = Object.assign( { timeZone: timezone }, timeLongOptions );

    if ( !isNaN( date.getTime() ) ) {
        retVal = new Intl.DateTimeFormat( undefined, cTimeOptions ).format( date );
    }

    return retVal;
}

/**
 * Returns the converted timerule for timezone.
 *
 * @param  {String}                 value                     The value to format.
 * @param  {String}                 timezone                  The timezone to format the value at.
 * @param  {String}                 language                  The language identifier to format the value with.
 *
 * @return {String}                                           The formatted value.
 */
export const formatTimerule = ( value : string, timezone : string = 'Europe/Berlin', language? : string ) : string => {
    // TODO: set correct type
    const ruleDetails : any = icalParser.getRuleDetails( value, timezone, language );

    let retVal = '';

    if ( ruleDetails?.rrule ) {
        retVal = ruleDetails.rrule.text;
    }

    return retVal;
}

/**
 * Returns the converted datetime for timezone and take care of our special value (untilFuture).
 *
 * @param  {Date|String}                 value                     The value to format.
 * @param  {String}                 timezone                  The timezone to format the value at.
 *
 * @return {String}                                           The formatted value.
 */
export const formatTimestamp = ( value : Date | string, timezone : string = 'Europe/Berlin' ) : string => {
    let retVal = '';

    const date : Date = typeof value === 'string'
              ? moment( value, [ 'YYYY-MM-DD HH:mm:ss.SSSSSS', 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm' ] ).toDate()
              : value,
          year        = !isNaN( date.getTime() )
              ? date.getFullYear()
              : 0;

    if ( year === 9999 || year === 10000 ) {
        retVal = __( 'YN.base.common.timestamp.untilFuture' );
    }
    else {
        const cDateOptions : Intl.DateTimeFormatOptions = Object.assign( { timeZone: timezone }, dateOptions ),
              cTimeOptions : Intl.DateTimeFormatOptions = Object.assign( { timeZone: timezone }, timeOptions );

        if ( !isNaN( date.getTime() ) ) {
            retVal = new Intl.DateTimeFormat( undefined, cDateOptions ).format( date ) + ' ' + new Intl.DateTimeFormat( undefined, cTimeOptions ).format( date );
        }
    }

    return retVal;
}

/**
 * Returns the converted timestamp.
 *
 * @param  {moment.MomentInput}            value                     The value to format.
 *
 * @return {String}                                           The formatted value.
 */
export const formatDbTimestamp = ( value : moment.MomentInput ) : string => {
    return moment( value, [ 'YYYY-MM-DD HH:mm:ss.SSSSSS', 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm' ] ).utc().format();
}

/**
 * Returns the formatted value.
 *
 * @param  {String}            value                     The value to format.
 *
 * @return {String}            The formatted value.
 */
export const formatValue = ( value : string, format : any ) : string => {
    let retVal : string = value,
        tempDate : Date,
        tempData : string[],
        imgAttrs : { [ key : string ] : any },
        imgSrcSets : { [ key : string ] : string },
        imgClassName : string,
        unit : string;

    switch ( format.type ) {

        case 'DateTime':
            retVal = retVal.replaceAll( /[\u200B-\u200E\uFEFF]/g, '' );
            tempDate = moment( retVal, [ 'YYYY-MM-DDTHH:mm:ss.SSSZ', 'YYYY-MM-DD HH:mm:ss.SSSSSS', 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm' ] )
                // set to utc without adjusting
                .tz( 'utc', true )
                .toDate();

            if ( !isNaN( tempDate.getTime() ) ) {
                retVal = Intl.DateTimeFormat( format.locales, format.options ).format( tempDate );
            }
            else {
                retVal = value;
            }

            break;

        case 'Image':
            tempData = value.split( '::rn::' );
            imgAttrs = {
                style: 'vertical-align: bottom; max-width: none;'
            };
            imgSrcSets = {};
            imgClassName = '';
            unit = format.options?.unit ?? 'px';

            if ( format.options?.height ) {
                imgAttrs.style += ` height: ${format.options.height}${unit};`;
            }
            if ( format.options?.width ) {
                imgAttrs.style += ` width: ${format.options.width}${unit};`;
            }

            if ( format.options?.maxHeight ) {
                imgAttrs.style += ` max-height: ${format.options.maxHeight}${unit};`;
            }
            if ( format.options?.minHeight ) {
                imgAttrs.style += ` min-height: ${format.options.minHeight}${unit};`;
            }
            if ( format.options?.maxHeight ) {
                imgAttrs.style += ` max-width: ${format.options.maxHeight}${unit};`;
            }
            if ( format.options?.minHeight ) {
                imgAttrs.style += ` min-width: ${format.options.minHeight}${unit};`;
            }

            for ( const data of tempData ) {
                let matches = data.match( /^imageData\(\s*"key"\s*:\s*"(?<key>[^"]+)"\s*,\s*"value"\s*:\s*"(?<value>[^"]+)"/ );

                if ( matches?.groups ) {
                    if ( !imgSrcSets[ '1x' ] ) {
                        imgSrcSets[ '1x' ] = matches.groups.value;
                    }
                    imgSrcSets[ matches.groups.key ] = matches.groups.value;
                }

                matches = data.match( /^cssClass\(\s*(?<class>[^)]*)\s*\)/ );

                if ( matches?.groups ) {
                    imgClassName = matches.groups.class;
                }
            }

            // only imageData given (not wrapped and labeled)
            if ( !Object.keys( imgSrcSets ).length && imgClassName === '' && tempData.length === 1 ) {
                imgSrcSets[ '1x' ] = tempData[ 0 ];
            }

            if ( Object.keys( imgSrcSets ).length ) {
                imgAttrs.src = imgSrcSets[ '1x' ];
                imgAttrs.srcset = Object.keys( imgSrcSets )
                                        .map( k => `${imgSrcSets[ k ]} ${k}` )
                                        .join( ',' );

                retVal = `<img ${Object.keys( imgAttrs ).map( k => `${k}="${imgAttrs[ k ]}"` ).join( ' ' )}/>`;
            }
            else {
                retVal = `<span class="yn-icon ${imgClassName}" style="${imgAttrs.style}"></span>`;
            }
            break;

        case 'Number':
            retVal = retVal.replaceAll( /[\u200B-\u200E\uFEFF]/g, '' );
            retVal = Intl.NumberFormat( format.locales, format.options ).format( Number( retVal ) );
            break;

        case 'Text':
            break;
    }

    if ( format.prefix ) {
        retVal = format.prefix + retVal;
    }

    if ( format.suffix ) {
        retVal = retVal + format.suffix;
    }

    return retVal;
}
